import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Carousel, CarouselItem, makeStyles } from 'housecall-ui';
import ChevronLeftIcon from 'housecall-ui/dist/IconsAndImages/Icons/ChevronLeft';
import ChevronRightIcon from 'housecall-ui/dist/IconsAndImages/Icons/ChevronRight';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';
import moment, { Moment } from 'moment';

import { TimeSlot } from '../../../api/api';
import { CAROUSEL_BREAKPOINTS, CAROUSEL_SETTINGS } from '../../../components/consts';
import { SmallCardButton } from '../../../components/SmallCardButton';
import { useTimeSlotsDict } from '../../../hooks/useTimeSlots';
import { useOnlineBookingReducer } from '../../../state/useOnlineBookingReducer';
import { getPricingFormItemsFromCart, getServiceItemsFromCart } from '../../../state/utils';
import { getDaysInRange, isTheSameDay } from '../../../utils/dateUtils';

import { DayCard } from './DayCard';

const useStyles = makeStyles((theme: HCPTheme) => ({
  card: {
    height: theme.spacing(10.5),
    margin: `${theme.spacing(2)}px 0`,
    width: theme.spacing(4.5),
    minWidth: theme.spacing(4.5),
  },
  button: {
    height: '100%',
    width: '100%',
    minWidth: '0',
  },
  daysWrapper: {
    display: 'block',
    flex: '0 1 100%',
    minWidth: '0',
  },
  noMargin: {
    '& > div': {
      margin: '0',
    },
  },
}));

type Props = {
  startDate: Date;
  lastDate: Date;
  selectedDate?: Moment;
  onSelect: (newDate: string) => void;
};

type CarouselRef = {
  state: {
    breakpoint: number;
  };
  slickGoTo: (index: number) => void;
  slickPrev: () => void;
  slickNext: () => void;
};

const prepareDays = (timeSlotsDict?: { [k: string]: TimeSlot[] }) => (day: Date) => {
  const dayInString = moment(day).format('YYYY-MM-DD');
  const dayIsSelected = Boolean(dayInString);
  const hasTimeSlots = timeSlotsDict && !timeSlotsDict[dayInString]?.length;

  return {
    day,
    disabled: Boolean(dayIsSelected && hasTimeSlots),
  };
};

export const DayPicker = ({ startDate, lastDate, selectedDate, onSelect }: Props) => {
  const { card, button, daysWrapper, noMargin } = useStyles();
  const carousel = useRef<CarouselRef>(null);
  const { state } = useOnlineBookingReducer();
  const { token } = state;
  const services = getServiceItemsFromCart(state).map((cartItem) => ({
    uuid: cartItem.service.uuid,
    quantity: cartItem.quantity,
  }));
  const pricingForms = getPricingFormItemsFromCart(state).map((cartItem) => ({
    uuid: cartItem.pricingForm.uuid,
  }));

  const { data: timeSlotsDict } = useTimeSlotsDict({
    token,
    dateFrom: startDate,
    dateTo: lastDate,
    services,
    pricingForms,
  });

  const daysToPick = useMemo(() => getDaysInRange(startDate, lastDate), [startDate, lastDate]);
  const preparedDays = useMemo(() => daysToPick.map(prepareDays(timeSlotsDict)), [daysToPick, timeSlotsDict]);

  const settings = {
    ...CAROUSEL_SETTINGS,
    className: noMargin,
    ref: carousel,
  };

  useEffect(() => {
    /* center carousel on selected date */
    if (!selectedDate) return;

    const selectedIndex = daysToPick.findIndex((day) => isTheSameDay(day, selectedDate));
    const breakpointSlideCount: number =
      CAROUSEL_BREAKPOINTS.find((breakpoint) => breakpoint.breakpoint === carousel.current?.state.breakpoint)?.settings
        .slidesToScroll || 4.5;
    const centerOffset = breakpointSlideCount / 2;
    carousel.current?.slickGoTo(selectedIndex - centerOffset);
  }, [selectedDate, daysToPick]);

  return (
    <Box display="flex" flexWrap="noWrap" justifyContent="space-between">
      <SmallCardButton classes={{ card, button }} onClick={() => carousel.current?.slickPrev()}>
        <ChevronLeftIcon date-testid="chevron-left" />
      </SmallCardButton>
      <Box className={daysWrapper}>
        <Carousel settings={settings}>
          {preparedDays.map(({ day, disabled }) => (
            <CarouselItem key={day.getTime()}>
              <DayCard
                day={day}
                selected={selectedDate ? isTheSameDay(day, selectedDate) : false}
                onClick={onSelect}
                disabled={disabled}
              />
            </CarouselItem>
          ))}
        </Carousel>
      </Box>
      <SmallCardButton classes={{ card, button }} onClick={() => carousel.current?.slickNext()}>
        <ChevronRightIcon date-testid="chevron-right" />
      </SmallCardButton>
    </Box>
  );
};
